import { FC, useCallback, useEffect, useState } from 'react';
import { useGetIdFromName } from '../form';
import { useTableStoreValue } from './TableContext';

/**
 * Observes resizing and intersection of certain HTML elements in order to:
 *  1. toggle sticky column shadows
 *  2. set left/right offsets to secondary sticky columns (if there are more than one sticky column on either side)
 */
export const AdjustStickyColumns: FC<{
  tableName: string;
  scrollContainer?: HTMLElement;
}> = (props) => {
  const { tableName, scrollContainer } = props;

  const [id] = useTableStoreValue('id');
  const [, setHideLeftStickyShadow] = useTableStoreValue(
    'hideLeftStickyShadow'
  );
  const [, setHideRightStickyShadow] = useTableStoreValue(
    'hideRightStickyShadow'
  );

  const handleStickyColumnCoverageChange = useCallback(
    (selector: StickySelector, isCoveringOtherColumns: boolean) => {
      if (selector.endsWith('th.sticky-left')) {
        setHideLeftStickyShadow(!isCoveringOtherColumns);
      } else {
        setHideRightStickyShadow(!isCoveringOtherColumns);
      }
    },
    [setHideLeftStickyShadow, setHideRightStickyShadow]
  );

  const [leftOffsets, setLeftOffsets] = useState<number[]>([]);
  const [rightOffsets, setRightOffsets] = useState<number[]>([]);

  const getIdFromName = useGetIdFromName();
  const getStickySelector = useCallback(
    (
      tableName: string,
      selector: (typeof stickySelectors)[number]
    ): StickySelector => `#${getIdFromName(tableName)} ${selector}`,
    [getIdFromName]
  );

  useEffect(() => {
    const headerRow = scrollContainer?.querySelector('thead tr');

    if (!scrollContainer || !headerRow) {
      return;
    }

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ removedNodes, addedNodes }) => {
        removedNodes.forEach((node) => {
          for (const [index, observer] of intersectionObservers.entries()) {
            if ((observer as any).target === node) {
              observer.disconnect();
              intersectionObservers.splice(index, 1);
            }
          }
        });
        addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            stickySelectors.forEach((selector) => {
              const observer = createStickyColumnCoverageObserver(
                getStickySelector(tableName, selector),
                (isCoveringOtherColumns) =>
                  handleStickyColumnCoverageChange(
                    getStickySelector(tableName, selector),
                    isCoveringOtherColumns
                  )
              );
              if (observer) {
                intersectionObservers.push(observer);
              }
            });
          }
        });
      });
    });
    mutationObserver.observe(headerRow, { childList: true, subtree: false });

    const intersectionObservers = stickySelectors.reduce<
      IntersectionObserver[]
    >((acc, selector) => {
      const observer = createStickyColumnCoverageObserver(
        getStickySelector(tableName, selector),
        (isCoveringOtherColumns) =>
          handleStickyColumnCoverageChange(
            getStickySelector(tableName, selector),
            isCoveringOtherColumns
          )
      );
      return observer ? acc.concat(observer) : acc;
    }, []);

    const resizeObserver = new ResizeObserver(() => {
      stickySelectors.forEach((selector) => {
        const stickies = getStickyElements(selector, !isLeft(selector));

        const offsets = stickies.slice(1).map((_, index) => {
          const previousSticky = stickies[index];
          return (
            previousSticky.getBoundingClientRect().right -
            stickies[0].getBoundingClientRect().left -
            1 // -1 is to count for the negative CSS left/right position (defined in stickyStyle.ts)
          );
        });

        if (isLeft(selector)) {
          setLeftOffsets(offsets);
        } else {
          setRightOffsets(offsets);
        }
      });
    });
    resizeObserver.observe(scrollContainer);

    return () => {
      intersectionObservers.forEach((observer) => observer.disconnect());
      resizeObserver.disconnect();
    };
  }, [tableName, scrollContainer]);

  if (leftOffsets.length || rightOffsets.length) {
    const offsetRules = Array.of(
      ...leftOffsets.map(
        (offset, index) =>
          `#${id} .sticky-left:nth-of-type(${index + 2}) { left: ${offset}px; }`
      ),
      ...rightOffsets.map(
        (offset, index) =>
          `#${id} .sticky-right:nth-last-of-type(${
            index + 2
          }) { right: ${offset}px; }`
      )
    );
    return <style>{`${offsetRules.join('\n')}`}</style>;
  }

  return null;
};

const stickySelectors = ['th.sticky-left', 'th.sticky-right'] as const;
type StickySelector = `#${string} ${(typeof stickySelectors)[number]}`;

const isLeft = (selector: string) => selector.endsWith('left');

const getStickyElements = (selector: string, reverse: boolean) => {
  const elements = Array.from(document.querySelectorAll(selector));

  if (reverse) {
    elements.reverse();
  }

  return elements;
};

const createStickyColumnCoverageObserver = (
  selector: StickySelector,
  onChange: (isCoveringOtherColumns: boolean) => unknown
) => {
  const peripheralSticky = getStickyElements(
    selector,
    !isLeft(selector)
  ).shift();

  if (peripheralSticky) {
    const intersectionObserver = new IntersectionObserver(
      ([{ intersectionRatio }]) => {
        // intersectionRatio may be less than 1 because peripheral sticky cells have negative CSS left/right position (defined in stickyStyle.ts)
        onChange(intersectionRatio !== 1);
      },
      { threshold: [1] }
    );
    intersectionObserver.observe(peripheralSticky);
    (intersectionObserver as any).target = peripheralSticky;

    return intersectionObserver;
  }

  return null;
};
